import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error,
  Delete,
} from '@mui/icons-material';
import { filesApi, foldersApi } from '../../services/fileShareApi';

const FileUpload = ({ open, onClose, onSuccess, folderId, projectId }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Debug logging
  console.log('🔍 FileUpload: folderId =', folderId);
  console.log('🔍 FileUpload: projectId =', projectId);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
    setError(null);
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and Drop handlers
  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);

    const droppedFiles = Array.from(event.dataTransfer.files);
    const validFiles = droppedFiles.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      return ['.rvt', '.dwg', '.pdf', '.jpg', '.jpeg', '.png', '.gif'].includes(`.${extension}`);
    });

    if (validFiles.length !== droppedFiles.length) {
      setError(`Some files were skipped. Only RVT, DWG, PDF, and image files are allowed.`);
    }

    setFiles(prev => [...prev, ...validFiles]);
    setError(prev => prev ? prev : null);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    // Require either folderId (selected folder) or projectId (upload to root)
    if (!folderId && !projectId) {
      setError('Please select a folder before uploading.');
      return;
    }

    let effectiveFolderId = folderId;

    // When at root (no folder selected), resolve project's root folder
    if (!effectiveFolderId && projectId) {
      try {
        const rootResponse = await foldersApi.getRootByProject(projectId);
        const rootFolders = rootResponse?.data;
        if (!rootFolders || !Array.isArray(rootFolders) || rootFolders.length === 0) {
          setError('No root folder found. Please sync folders or create a folder first.');
          return;
        }
        effectiveFolderId = rootFolders[0].folderId;
      } catch (err) {
        console.error('Error fetching root folders:', err);
        setError('Could not load root folder. Please try again.');
        return;
      }
    }

    // Check if effectiveFolderId is a valid UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!effectiveFolderId || !uuidRegex.test(effectiveFolderId)) {
      setError(`Invalid folder ID. Please select a valid folder.`);
      console.error('Invalid folderId:', effectiveFolderId);
      return;
    }

    setUploading(true);
    setError(null);

    let lastEgnytePath = null;
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress(prev => ({ ...prev, [i]: 0 }));

        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folderId', effectiveFolderId);

        console.log('🔍 Uploading file to folderId:', effectiveFolderId);

        // Upload file via API
        const uploadResponse = await filesApi.upload(formData);

        // Mark as complete
        setUploadProgress(prev => ({ ...prev, [i]: 100 }));

        if (uploadResponse?.data?.egnytePath) {
          lastEgnytePath = uploadResponse.data.egnytePath;
        }
      }

      onSuccess(lastEgnytePath || null);
      setFiles([]);
      setUploadProgress({});
    } catch (err) {
      setError('Upload failed. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFiles([]);
      setUploadProgress({});
      setError(null);
      onClose();
    }
  };

  const getFileIcon = (file) => {
    const extension = file.name.split('.').pop().toLowerCase();
    switch (extension) {
      case 'rvt':
        return '🏗️';
      case 'dwg':
        return '📐';
      case 'pdf':
        return '📄';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️';
      default:
        return '📁';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CloudUpload sx={{ mr: 1 }} />
          Upload Files
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Drag and Drop Area */}
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-upload').click()}
          sx={{
            border: '2px dashed',
            borderColor: isDragOver ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            backgroundColor: isDragOver ? 'primary.light' : 'grey.50',
            transition: 'all 0.3s ease',
            mb: 3,
            cursor: 'pointer',
          }}
        >
          <CloudUpload
            sx={{
              fontSize: 48,
              color: isDragOver ? 'primary.main' : 'grey.400',
              mb: 2
            }}
          />
          <Typography variant="h6" gutterBottom>
            {isDragOver ? 'Drop files here' : 'Drag & Drop files here'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            or click the button below to select files
          </Typography>

          <input
            accept=".rvt,.dwg,.pdf,.jpg,.jpeg,.png,.gif"
            style={{ display: 'none' }}
            id="file-upload"
            multiple
            type="file"
            onChange={handleFileSelect}
          />
          <label htmlFor="file-upload">
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUpload />}
              disabled={uploading}
              onClick={(e) => e.stopPropagation()}
            >
              Select Files
            </Button>
          </label>

          <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
            Supported formats: RVT, DWG, PDF, JPG, PNG, GIF
          </Typography>
        </Box>

        {files.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Selected Files ({files.length})
            </Typography>
            
            <List>
              {files.map((file, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Typography sx={{ fontSize: '1.5rem' }}>
                      {getFileIcon(file)}
                    </Typography>
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={file.name}
                    secondary={formatFileSize(file.size)}
                  />
                  
                  {uploading ? (
                    <Box sx={{ width: 200, mr: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress[index] || 0}
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {uploadProgress[index] || 0}%
                      </Typography>
                    </Box>
                  ) : (
                    <IconButton
                      onClick={() => handleRemoveFile(index)}
                      disabled={uploading}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={files.length === 0 || uploading}
          startIcon={uploading ? <LinearProgress /> : <CloudUpload />}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileUpload;

